/** Probate 174 — Stream F */
import DocumentUploadSection from '../../settlement/DocumentUploadSection';
import {
  probateFieldLabelClass,
  probateInputClass,
  ProbateBackLink,
  ProbateChecklistCard,
  ProbateChecklistProgress,
  ProbateContextBlock,
} from './probateStepUi';
import { useProbateDocuments } from './useProbateDocuments';
import { useProbateStep } from './useProbateStep';

const STEP_ID = 'prob-4';
const FORM_FIELDS =
  'hearingDate,bondRequired,bondAmount,oathTakenDate,certifiedCopiesOrdered,lettersIssuedDate';

export default function SFrame174() {
  const idsA = ['prob-4-item-1', 'prob-4-item-2', 'prob-4-item-3'];
  const idsB = ['prob-4-item-4', 'prob-4-item-5'];
  const idsC = ['prob-4-item-6', 'prob-4-item-7', 'prob-4-item-8', 'prob-4-item-9'];

  const {
    estateId,
    isArchived,
    error,
    stepDef,
    g,
    checklistDefs,
    checklistCompleted,
    checklistTotal,
    checkedIds,
    toggleItem,
    formValues,
    setField,
    touchActivity,
  } = useProbateStep(STEP_ID, FORM_FIELDS);

  const pick = (ids: string[]) => checklistDefs.filter((c) => ids.includes(c.id));

  const ro = !!isArchived;
  const { documents, upload, remove } = useProbateDocuments(estateId, STEP_ID, ro);
  const bondYes = formValues.bondRequired === 'yes';

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      <ProbateBackLink estateId={estateId} />
      {error ? (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>
      ) : null}
      <h1 className="text-2xl font-semibold text-stone-800 mb-2">{stepDef?.title}</h1>
      {g?.point && g?.goal ? <ProbateContextBlock point={g.point} goal={g.goal} /> : null}
      <ProbateChecklistProgress completed={checklistCompleted} total={checklistTotal} />

      {/* A */}
      <h2 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Section A — Court outcome</h2>
      <div className="bg-white rounded-xl border border-stone-200 p-5 mb-3 space-y-4">
        <h3 className="text-sm font-semibold text-stone-800">Inputs</h3>
        <div>
          <label className={probateFieldLabelClass} htmlFor="hearingDate">Hearing date</label>
          <input id="hearingDate" type="date" className={probateInputClass} disabled={ro}
            value={formValues.hearingDate ?? ''} onChange={(e) => setField('hearingDate', e.target.value)} />
        </div>
        <div>
          <span className={probateFieldLabelClass}>Bond required</span>
          <div className="flex flex-wrap gap-4">
            <label className="inline-flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
              <input type="radio" name="bondRequired" className="h-4 w-4 border-stone-300 text-cyan-600" disabled={ro}
                checked={formValues.bondRequired === 'yes'} onChange={() => setField('bondRequired', 'yes')} />
              Yes
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
              <input type="radio" name="bondRequired" className="h-4 w-4 border-stone-300 text-cyan-600" disabled={ro}
                checked={formValues.bondRequired === 'no'} onChange={() => setField('bondRequired', 'no')} />
              No
            </label>
          </div>
        </div>
        <div className={`${!bondYes ? 'opacity-50 pointer-events-none' : ''}`}>
          <label className={probateFieldLabelClass} htmlFor="bondAmount">Bond amount</label>
          <input id="bondAmount" className={probateInputClass} disabled={ro || !bondYes}
            value={formValues.bondAmount ?? ''} onChange={(e) => setField('bondAmount', e.target.value)} placeholder="Currency / coverage" />
        </div>
        <div>
          <label className={probateFieldLabelClass} htmlFor="oathTakenDate">Oath taken (date)</label>
          <input id="oathTakenDate" type="date" className={probateInputClass} disabled={ro}
            value={formValues.oathTakenDate ?? ''} onChange={(e) => setField('oathTakenDate', e.target.value)} />
        </div>
      </div>
      <h4 className="text-sm font-semibold text-stone-800 uppercase tracking-wide mb-2">Checklist</h4>
      <ProbateChecklistCard items={pick(idsA)} checkedIds={checkedIds} toggleItem={toggleItem} disabled={ro} />

      {/* B */}
      <h2 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mt-6 mb-2">Section B — Letters tracking</h2>
      <div className="bg-white rounded-xl border border-stone-200 p-5 mb-3 space-y-4">
        <h3 className="text-sm font-semibold text-stone-800">Inputs</h3>
        <div>
          <label className={probateFieldLabelClass} htmlFor="certifiedCopiesOrdered">Number of certified copies ordered</label>
          <input id="certifiedCopiesOrdered" inputMode="numeric" className={probateInputClass} disabled={ro}
            value={formValues.certifiedCopiesOrdered ?? ''}
            onChange={(e) => setField('certifiedCopiesOrdered', e.target.value)} />
        </div>
        <div>
          <label className={probateFieldLabelClass} htmlFor="lettersIssuedDate">Letters issued (date)</label>
          <input id="lettersIssuedDate" type="date" className={probateInputClass} disabled={ro}
            value={formValues.lettersIssuedDate ?? ''} onChange={(e) => setField('lettersIssuedDate', e.target.value)} />
        </div>
      </div>
      <h4 className="text-sm font-semibold text-stone-800 uppercase tracking-wide mb-2">Checklist</h4>
      <ProbateChecklistCard items={pick(idsB)} checkedIds={checkedIds} toggleItem={toggleItem} disabled={ro} />

      {/* C */}
      <h2 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mt-6 mb-2">Section C — Distribution to institutions</h2>
      <h4 className="text-sm font-semibold text-stone-800 uppercase tracking-wide mb-2">Checklist</h4>
      <ProbateChecklistCard items={pick(idsC)} checkedIds={checkedIds} toggleItem={toggleItem} disabled={ro} />

      <div className="bg-white rounded-xl border border-stone-200 p-5 mt-4">
        <DocumentUploadSection
          documents={documents}
          onUpload={async (f) => { await touchActivity(); await upload(f); }}
          onDelete={ro ? undefined : (id) => remove(id)}
          disabled={ro}
        />
        <p className="text-xs text-stone-500 mt-3">Upload letters testamentary and court orders authorizing authority.</p>
      </div>
    </div>
  );
}
